import {
  RUN_CAMPEON_MICROTEXT_BANK,
  RUN_CAMPEON_MICROTEXT_RULES,
  type RunCampeonMicrotextCategory
} from "../microtextConfig";
import type { PickupData } from "../types";
import type { StoryBiomeId } from "../story/storyConfig";

export interface MicrotextMessage {
  text: string;
  durationMs: number;
}

interface MicrotextUpdateInput {
  dt: number;
  progress: number;
  musicEnergy: number;
  rearThreat: number;
  danger: number;
  biome: StoryBiomeId;
}

export class MicrotextSystem {
  private usedPhrases = new Set<string>();
  private cooldown = 0;
  private lowEnergyWasActive = false;
  private criticalEnergyWasActive = false;
  private finalStretchWasActive = false;
  private lastBiome?: StoryBiomeId;
  private pickupChain = 0;
  private pickupChainTimer = 0;

  reset() {
    this.usedPhrases.clear();
    this.cooldown = 0;
    this.lowEnergyWasActive = false;
    this.criticalEnergyWasActive = false;
    this.finalStretchWasActive = false;
    this.lastBiome = undefined;
    this.pickupChain = 0;
    this.pickupChainTimer = 0;
  }

  update(input: MicrotextUpdateInput): MicrotextMessage | null {
    this.cooldown = Math.max(0, this.cooldown - input.dt);
    this.pickupChainTimer = Math.max(0, this.pickupChainTimer - input.dt);
    if (this.pickupChainTimer <= 0) this.pickupChain = 0;

    const critical = input.musicEnergy <= RUN_CAMPEON_MICROTEXT_RULES.criticalEnergyThreshold;
    const low = input.musicEnergy <= RUN_CAMPEON_MICROTEXT_RULES.lowEnergyThreshold;
    const spiritsClose = Math.max(input.rearThreat, input.danger) >= RUN_CAMPEON_MICROTEXT_RULES.spiritsCloseThreshold;
    const finalStretch = input.progress >= RUN_CAMPEON_MICROTEXT_RULES.finalStretchProgress;

    if (critical && !this.criticalEnergyWasActive) {
      this.criticalEnergyWasActive = true;
      return this.tryPick("criticalEnergy");
    }
    this.criticalEnergyWasActive = critical;

    if (spiritsClose) {
      const message = this.tryPick("spiritsClose");
      if (message) return message;
    }

    if (low && !this.lowEnergyWasActive) {
      this.lowEnergyWasActive = true;
      return this.tryPick("energyDecay");
    }
    this.lowEnergyWasActive = low;

    if (finalStretch && !this.finalStretchWasActive) {
      this.finalStretchWasActive = true;
      return this.tryPick(Math.random() > 0.5 ? "finalStretch" : "packVoice");
    }

    if (this.lastBiome !== input.biome) {
      this.lastBiome = input.biome;
      if (input.biome === "ancient_ruins" || input.biome === "corrupted_pressure_zone" || input.biome === "final_spirit_chase") {
        return this.tryPick("forbiddenForest");
      }
    }

    return null;
  }

  onPickup(pickup: PickupData): MicrotextMessage | null {
    this.pickupChain += 1;
    this.pickupChainTimer = 2.25;

    if (this.pickupChain >= 3) return this.tryPick("comboPickup");
    if (this.isStrongPickup(pickup)) return this.tryPick("strongPickup");
    return this.tryPick("positivePickup");
  }

  onChampionBoostStart(): MicrotextMessage | null {
    return this.tryPick("boostStart");
  }

  onPlayerHit(): MicrotextMessage | null {
    return this.tryPick("hitOrMistake");
  }

  private tryPick(category: RunCampeonMicrotextCategory): MicrotextMessage | null {
    if (this.cooldown > 0) return null;
    const phrase = this.getRandomUnusedPhrase(category);
    if (!phrase) return null;
    this.cooldown = RUN_CAMPEON_MICROTEXT_RULES.minSecondsBetweenPhrases;
    return { text: phrase, durationMs: this.getDurationMs(category) };
  }

  private getRandomUnusedPhrase(category: RunCampeonMicrotextCategory) {
    const pool = RUN_CAMPEON_MICROTEXT_BANK[category].filter((phrase) => !this.usedPhrases.has(phrase));
    if (pool.length === 0) return null;
    const phrase = pool[Math.floor(Math.random() * pool.length)];
    this.usedPhrases.add(phrase);
    return phrase;
  }

  private getDurationMs(category: RunCampeonMicrotextCategory) {
    if (category === "finalStretch" || category === "packVoice") return RUN_CAMPEON_MICROTEXT_RULES.finalDuration * 1000;
    if (category === "spiritsClose" || category === "hitOrMistake") return RUN_CAMPEON_MICROTEXT_RULES.cruelDuration * 1000;
    if (category === "energyDecay" || category === "criticalEnergy") return RUN_CAMPEON_MICROTEXT_RULES.negativeDuration * 1000;
    return RUN_CAMPEON_MICROTEXT_RULES.positiveDuration * 1000;
  }

  private isStrongPickup(pickup: PickupData) {
    return (
      pickup.syncType === "champion_core" ||
      pickup.syncType === "champion_burst" ||
      pickup.syncType === "first_boost_core" ||
      pickup.syncType === "final_champion_core" ||
      pickup.syncType === "pack_echo_core"
    );
  }
}
